import { supabase } from './supabase';

/** Normalized decode result returned by the vehicle-decode edge function. */
export interface VinDecodeResult {
  vin: string;
  valid: boolean;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  /** Full style line when available, e.g. "4x4 4dr Crew Cab 5.8 ft. SB". */
  style: string | null;
  bodyClass: string | null;
  doors: number | null;
  engine: string | null;
  fuelType: string | null;
  drivetrain: string | null;
  transmission: string | null;
  manufacturer: string | null;
  plantCountry: string | null;
  /** Factory exterior color name from OEM build data, e.g. "Wind Chill Pearl". */
  exteriorColor: string | null;
  /** Hex swatch for the exterior color when available. */
  exteriorColorHex: string | null;
  /** Factory interior color name. */
  interiorColor: string | null;
  /** Factory paint option code, e.g. Toyota "089". */
  paintCode: string | null;
  sources: string[];
  error?: string;
}

export function isPlausibleVin(vin: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim().toUpperCase());
}

/**
 * Decode a VIN via the vehicle-decode edge function (NHTSA + auto.dev,
 * cached server-side so repeat lookups are instant and free).
 * Throws on transport errors; invalid VINs come back as { valid: false, error }.
 */
export async function decodeVin(vin: string): Promise<VinDecodeResult> {
  const { data, error } = await supabase.functions.invoke('vehicle-decode', {
    body: { vin: vin.trim().toUpperCase() },
  });
  if (error) throw new Error(error.message || 'VIN decode failed');
  return data as VinDecodeResult;
}

/** One-line summary of a decode, e.g. "2022 Chevrolet Silverado 1500 ZR2 · 5.3L V8 · 4WD · Summit White (GAZ)". */
export function vinDecodeSummary(d: VinDecodeResult): string {
  const identity = [d.year, d.make, d.model, d.trim].filter(Boolean).join(' ');
  const color = d.exteriorColor
    ? d.paintCode
      ? `${d.exteriorColor} (${d.paintCode})`
      : d.exteriorColor
    : null;
  const details = [d.engine, d.drivetrain, color].filter(Boolean).join(' · ');
  return details ? `${identity} · ${details}` : identity;
}

/**
 * Factory trim levels for a year/make/model via the vehicle-trims edge
 * function (AI-sourced, permanently cached server-side). Returns [] when the
 * lineup is unknown — callers should fall back to free-text trim entry.
 */
export async function fetchTrimOptions(
  year: number,
  make: string,
  model: string,
): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke('vehicle-trims', {
    body: { year, make, model },
  });
  if (error || !Array.isArray(data?.trims)) return [];
  return (data.trims as unknown[]).filter(
    (t): t is string => typeof t === 'string' && t.trim().length > 0,
  );
}
